import type { WritePolicy } from './api.js';

/**
 * What to tell a customer when writing is blocked, and where to send them.
 *
 * ⚠ EXTRACTED BECAUSE THE PRODUCT ONLY SAID IT IN ONE PLACE. Until 15
 * September 2026 the "no active subscription" message existed exactly once:
 * inside the grid's PREVIEW panel. So a customer who signed up, connected
 * their instance and started work was told nothing at all — not on the
 * instance page they landed on after authorizing, not on the grid they spent
 * twenty minutes editing — until they had made every change they intended to
 * make and pressed Preview. The product's own gate was the last thing they
 * met, after the work, instead of the first thing, before it.
 *
 * Found by recording the signup walkthrough: the video showed a freshly
 * connected instance with no route to paying and no hint one was needed.
 *
 * ⚠⚠ KEY OFF `blockedReason`, NEVER OFF `disabled`. Both block states set
 * `disabled: true`, so a `disabled` check cannot tell an unpaid account from a
 * support stop — and it would tell a customer who has simply not chosen a plan
 * yet to "contact support", which is wrong, unhelpful, and a ticket we then
 * have to answer. The server distinguishes them; the UI must not re-merge them.
 */
export interface WriteBlockedNotice {
  heading: string;
  body: string;
  /** Only the subscription block has anywhere useful to send someone. */
  action: { to: string; label: string } | null;
}

/**
 * `place` changes only the reassurance, not the rule:
 * - `instance` — nothing has been typed yet, so say what will happen later.
 * - `edits` — unsaved work is on screen, so the first thing to answer is
 *   whether it has just been lost. It has not.
 */
export function writeBlockedNotice(
  policy: WritePolicy | null,
  tenantId: string,
  place: 'instance' | 'edits'
): WriteBlockedNotice | null {
  if (!policy || policy.blockedReason === null) return null;

  if (policy.blockedReason === 'subscription') {
    return {
      heading: 'No active subscription.',
      // ⚠ "THIS SCREEN", NOT "THE GRID". Seen on the merge screen while
      // walking on 19 September: MergePage renders this same notice, and there
      // is no grid there — what a customer loses by leaving is the survivor and
      // the field choices they just made. Naming the grid told them the wrong
      // thing about the wrong screen.
      //
      // ⚠ THIS USED TO SAY the changes would "still be here" after choosing a
      // plan, and that was not true. Edits live in the grid page's own state,
      // so following this link unmounts the page and discards them — the
      // notice was promising exactly what clicking it took away. The link now
      // goes through the same leave-confirmation as every other way out (see
      // onNavigate below), and the wording says what actually happens.
      body:
        place === 'edits'
          ? 'Your edits are safe while you stay on this screen, but nothing can ' +
            'be written to Acumatica until this account has a plan. Opening the ' +
            'billing page leaves this screen, which discards them — so copy ' +
            'down anything you need first, or open it in a new tab.'
          : 'You can browse this instance and prepare changes, but nothing can be ' +
            'written to Acumatica until this account has a plan.',
      action: { to: `/tenants/${tenantId}/billing`, label: 'Choose a plan' },
    };
  }

  return {
    heading: 'Writing is turned off for this instance.',
    body:
      (place === 'edits'
        ? 'Your edits are safe on this screen, but nothing can be committed. '
        : 'You can browse this instance and prepare changes, but nothing can be ' +
          'written to Acumatica. ') +
      'This is a manual stop, not a fault with your data — contact support and we ' +
      'will tell you why it is on.',
    action: null,
  };
}
